import { useCallback, useMemo } from 'react';
import { commandsCtx, editorViewCtx } from '@milkdown/core';
import { useInstance } from '@milkdown/react';
import { wrapInOrderedListCommand, wrapInBulletListCommand, liftListItemCommand, bulletListSchema, orderedListSchema, } from '@milkdown/preset-commonmark';
import { Fragment } from '@milkdown/prose/model';
import { TextSelection } from '@milkdown/prose/state';
import { useNotesToolbarContext } from '../../context/notes/notes_toolbar_context';
import { detectSelectedListType, isValidListType } from '../../util/notes';
import theme from '../../styles/theme';
import { useEditorStateContext } from '../../context/notes/notes_editor_state_context';
import { ORDERED_LIST_BUTTON_SUFFIX, BULLET_LIST_BUTTON_SUFFIX, } from '../../components/notes/consts';
export function useListButtons() {
    const [isLoading, getEditorInstance] = useInstance();
    const { listsContext } = useNotesToolbarContext();
    const { captureButtonClick, inCallOrPostCall, readOnlyYn, displayModeYn } = useEditorStateContext();
    const areButtonsDisabled = readOnlyYn ||
        displayModeYn ||
        isLoading ||
        !listsContext.canInsertList ||
        listsContext.selectedListType === 'unknown';
    const iconHoverFillColor = areButtonsDisabled ? undefined : theme.colors.blue50;
    const isBulletActive = listsContext.selectedListType === 'bullet_list';
    const isOrderedActive = listsContext.selectedListType === 'ordered_list';
    const handleOrderedOnClick = useCallback(() => {
        if (!isLoading) {
            // immediately fire the click function created
            createToggleListFunction(getEditorInstance, isLoading, 'ordered_list')();
            typeof captureButtonClick === 'function' &&
                captureButtonClick(`${inCallOrPostCall}-${ORDERED_LIST_BUTTON_SUFFIX}`);
        }
    }, [isLoading, getEditorInstance, captureButtonClick, inCallOrPostCall]);
    const handleBulletOnClick = useCallback(() => {
        if (!isLoading) {
            // immediately fire the click function created
            createToggleListFunction(getEditorInstance, isLoading, 'bullet_list')();
            typeof captureButtonClick === 'function' &&
                captureButtonClick(`${inCallOrPostCall}-${BULLET_LIST_BUTTON_SUFFIX}`);
        }
    }, [isLoading, getEditorInstance, captureButtonClick, inCallOrPostCall]);
    return useMemo(() => {
        return {
            handleOrderedOnClick,
            handleBulletOnClick,
            iconHoverFillColor,
            isEditorLoadingYn: isLoading,
            isBulletActive,
            isOrderedActive,
            areButtonsDisabled,
        };
    }, [
        isLoading,
        handleOrderedOnClick,
        handleBulletOnClick,
        iconHoverFillColor,
        isBulletActive,
        isOrderedActive,
        areButtonsDisabled,
    ]);
}
/**
 * Utility Functions
 */
/**
 * Build valid attrs for target list type
 * @note Attrs is read only, so this ensures we mirror the shape [attr: string]: any; with an explicit "any"
 */
function buildListAttrs(targetListType, sourceAttrs) {
    const validAttrs = {
        spread: sourceAttrs.spread ?? false,
    };
    if (targetListType === 'ordered_list') {
        validAttrs.order = sourceAttrs.order ?? 1;
    }
    return validAttrs;
}
/**
 * Updates only the immediate list_item children's attrs
 * Does NOT recurse into nested lists - preserves them as-is
 */
function updateListItemAttrs(content, targetListType) {
    const nodes = [];
    content.forEach(node => {
        if (node.type.name === 'list_item') {
            // Update this list_item's attrs for the target type
            const newAttrs = targetListType === 'bullet_list'
                ? { listType: 'bullet', spread: node.attrs.spread }
                : { listType: 'ordered', spread: node.attrs.spread, label: node.attrs.label };
            // Keep content as-is (don't recurse into nested lists)
            nodes.push(node.type.create(newAttrs, node.content, node.marks));
        }
        else {
            nodes.push(node);
        }
    });
    return Fragment.from(nodes);
}
/**
 * Creates editor transaction to update lists with appropriate conversion
 * @param listPos - number
 * @param node - node associated with transaction
 * @param ctx - editor context object
 * @param targetListType - The type of list to toggle to ('bullet_list' | 'ordered_list')
 * @returns Milkdown editor transaction to dispatch for single execution
 */
function createEditorTransaction(listPos, node, ctx, targetListType) {
    const targetListSchema = targetListType === 'ordered_list'
        ? orderedListSchema.type(ctx)
        : bulletListSchema.type(ctx);
    const view = ctx.get(editorViewCtx);
    const validAttrs = buildListAttrs(targetListType, node.attrs);
    const updatedContent = updateListItemAttrs(node.content, targetListType);
    const newList = targetListSchema.create(validAttrs, updatedContent, node.marks);
    // create a transaction that replaces the list node at the given position
    return view.state.tr.replaceWith(listPos, listPos + node.nodeSize, newList);
}
/**
 * A helper that finds the list node containing the cursor by traversing up
 * @param targetListType
 * @param sourceListSchema
 * @param ctx
 * @returns the list position
 */
function findListNode(sourceListSchema, ctx, actionCallback) {
    const view = ctx.get(editorViewCtx);
    const { selection } = view.state;
    const { $anchor } = selection;
    for (let depth = $anchor.depth; depth > 0; depth--) {
        const node = $anchor.node(depth);
        if (node.type === sourceListSchema) {
            const listPos = $anchor.before(depth);
            // fire an action if one is provided
            actionCallback?.(listPos, node);
            return listPos;
        }
    }
}
/**
 * Factory that generates a click handler to toggle between bullet or ordered list
 * in an editor instance that handles deep lists
 * @param getEditorInstance - Function returning the current editor instance
 * @param isLoading - Editor loading state
 * @param targetListType - The type of list to toggle to ('bullet_list' | 'ordered_list')
 * @returns Click handler function
 */
export function createToggleListFunction(getEditorInstance, isLoading, targetListType) {
    return function () {
        if (isLoading)
            return;
        const editor = getEditorInstance();
        editor.action(ctx => {
            const view = ctx.get(editorViewCtx);
            const commandManager = ctx.get(commandsCtx);
            const { selection } = view.state;
            const currentListType = detectSelectedListType(ctx, selection);
            // Case 1: Toggle off - cursor in same list type
            if (currentListType === targetListType) {
                commandManager.call(liftListItemCommand.key);
                view.focus();
                return;
            }
            const sourceListSchema = targetListType === 'ordered_list'
                ? bulletListSchema.type(ctx)
                : orderedListSchema.type(ctx);
            // Case 2: Empty selection (cursor) - convert containing list at current depth
            if (selection.empty &&
                isValidListType(currentListType) &&
                currentListType !== targetListType) {
                // Find the list node containing the cursor by traversing up
                findListNode(sourceListSchema, ctx, (listPos, node) => {
                    // Store cursor position RELATIVE to list start
                    const cursorOffset = selection.anchor - listPos;
                    // Convert ONLY this list node at this depth
                    let tr = createEditorTransaction(listPos, node, ctx, targetListType);
                    // Restore cursor at same relative position
                    const newCursorPos = listPos + cursorOffset;
                    tr = tr.setSelection(TextSelection.create(tr.doc, newCursorPos));
                    view.dispatch(tr);
                    view.focus();
                });
                return;
            }
            // Case 3: Range selection - collect and convert all source-type lists in range
            const listsToConvert = [];
            view.state.doc.nodesBetween(selection.from, selection.to, (node, pos) => {
                if (node.type === sourceListSchema) {
                    listsToConvert.push({ pos, node });
                }
                return true;
            });
            // Case 3.1 No lists found - create new one
            if (listsToConvert.length === 0) {
                const wrapCommand = targetListType === 'ordered_list'
                    ? wrapInOrderedListCommand.key
                    : wrapInBulletListCommand.key;
                commandManager.call(wrapCommand);
                view.focus();
                return;
            }
            // Case 3.2: Convert existing lists in selection
            let tr = view.state.tr;
            // Preserve the previous anchor of the selection
            const previousAnchor = selection.$anchor;
            // Convert all lists (reverse order for position stability)
            // NOTE: this currently does not work with nested lists due to
            // our detection failing to identify nested lists of the same type.
            // The experience today is that the toggle is DISABLED as
            // the list type returns "unknown".
            // TODO: work captured here: https://ftdr.atlassian.net/browse/ST-9164
            for (let i = listsToConvert.length - 1; i >= 0; i--) {
                const { pos, node } = listsToConvert[i];
                tr = createEditorTransaction(pos, node, ctx, targetListType);
            }
            // Reset anchor position after transformations
            const newPos = Math.max(1, Math.min(previousAnchor.after() - 1, tr.doc.content.size - 1));
            tr = tr.setSelection(TextSelection.create(tr.doc, newPos));
            view.dispatch(tr);
            view.focus();
        });
    };
}
//# sourceMappingURL=use_list_buttons.js.map